import React, { useContext, useEffect, useState } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { useForm } from "react-hook-form"

import { DealershipLoader } from "../../components/common/dealershipLoader/dealershipLoader"
import { Button } from "../../components/common/button/Button"
import { CountryFlag } from "../../components/common/countryFlag/countryFlag"
import { Input } from "../../components/common/input/Input"
import { Seperator } from "../../components/common/seperator/seperator"
import { TextLink } from "../../components/common/TextLink/TextLink"
import { AppContext, AppContextInterface } from "../../state/ContextProvider"
import { GeoLocation } from "../../utils/geolocation"
import { GroupIds } from "../../utils/group-utils"
import TermsAndConditions from "../../components/TermsAndConditions/TermsAndConditions"
import { ContactNumber } from "../../components/common/contactNumber/contactNumber"
import { checkPath } from "../../utils/validation-utils"
import { Dropdown } from "../../components/common/dropdown"
import { getDealershipUrl, getGlobalDealershipId } from "../../utils/dealership-utils"
import GAHiddenInputs from "../../components/common/gaHiddenInputs/gaHiddenInputs"

type FormData = {
  vehicleType: boolean
  model: string
  postcode: string
  brand: string
  whichVehicleType: string
  firstName: string
  lastName: string
  email: string
  contactNumber: string
  countryCode: string
  termsConditions: boolean
  type: string
}

const CouldntFindVehicle: React.FC = () => {
  const history = useHistory()
  const { search } = useLocation()
  const {
    actions: {
      addContactDetails,
      addWhichVehicle,
      addTermsConditions,
      addWhichVehicleType,
      addClosestDealerships,
      addAppointmentDetails,
      addDealershipGuid,
      addDealershipUrl,
    },
    state: {
      contactDetails,
      termsConditions,
      closestDealerships,
      appointmentDetails: { dealership },
      appointmentDetails,
      contactDetails: {
        firstName,
        lastName,
        email,
        countryCode,
        postcode,
        contactNumber,
      },
      whichVehicle,
      whichVehicle: { model, brand },
      whichVehicleType,
      groupId,
    },
    api: { getLead },
  } = useContext(AppContext) as AppContextInterface

  const { handleSubmit, register, errors, control, watch, setValue } =
    useForm<FormData>({
      mode: "all",
      defaultValues: {
        model: whichVehicle?.model,
        brand: whichVehicle?.brand,
        whichVehicleType,
        firstName: contactDetails?.firstName,
        lastName: contactDetails?.lastName,
        email: contactDetails?.email,
        contactNumber: contactDetails?.contactNumber,
        countryCode: contactDetails?.countryCode,
        postcode: contactDetails?.postcode,
        termsConditions: termsConditions,
        type: whichVehicleType,
      },
    })

  const errorMessage = {
    vehicleManufacturer: "Please enter a valid manufacturer",
    vehicleModel: "Please enter a valid model",
    firstName: "Please enter a valid first name",
    lastName: "Please enter a valid last name",
    contactNumber: "Please enter a valid mobile number",
    email: "Please enter a valid email",
    dealership:
      "Ooops something has gone wrong, please try again or select another dealership",
    postcode: "Please enter a valid postcode",
  }

  const onSubmit = (formData: any) => {
    addContactDetails({
      ...contactDetails,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      contactNumber: formData.contactNumber,
      countryCode: formData.countryCode,
      postcode: formData.postcode,
      termsConditions: formData.termsConditions,
      vehicleType: formData.vehicleType,
    })
  }

  const mobileOrLandlineNumber = watch("contactNumber")

  const [postCodeError, setPostCodeError] = useState(false)
  const [dealershipCtaDisabled, setDealershipCtaDisabled] = useState(true)
  const [validPostcode, setValidPostcode] = useState(false)
  const [isButtonClicked, setButtonClicked] = useState(false)
  const [isSubmitted, setSubmitted] = useState(false)
  const [dealershipError, setDealershipError] = useState(false)
  const [dealershipGuidError, setDealershipGuidError] = useState(false)
  const [isFindMyDealershipClicked, setFindMyDealershipClicked] = useState(false)
  const [validPostCodeError, setValidPostCodeError] = useState(false)

  const errorForNoDealershipGuid = (error: boolean) => {
    error ? setDealershipGuidError(true) : setDealershipGuidError(false);
  };

  useEffect(() => {
    return mobileOrLandlineNumber
      ? addContactDetails({
        ...contactDetails,
        contactNumber: mobileOrLandlineNumber,
      })
      : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileOrLandlineNumber])

  useEffect(() => {
    const findDealershipGuid = () => {
      if (dealership) {
        const dealershipUrl = getDealershipUrl(closestDealerships, dealership)
        const globalDealershipId = getGlobalDealershipId(
          closestDealerships,
          dealership
        )
        if (globalDealershipId === null) {
          setDealershipError(true)
          errorForNoDealershipGuid(true)
        } else {
          addDealershipUrl(dealershipUrl)
          errorForNoDealershipGuid(false)
          addDealershipGuid(globalDealershipId)
          setDealershipError(false)
        }
      }
    }
    findDealershipGuid()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closestDealerships, dealership])

  useEffect(() => {
    if (postCodeError && validPostcode) {
      setValidPostCodeError(true)
    }
  }, [postCodeError, validPostcode])

  const radiosErrorClass = errors["type"] ? "red" : ""

  const checkForEmptyFields =
    firstName &&
    lastName &&
    model &&
    brand &&
    contactNumber &&
    postcode &&
    email &&
    termsConditions &&
    whichVehicleType &&
    dealership

  return (
    <div className="no-vehicle-container">
      <div className="no-vehicle-heading-container">
        <h2 className="no-vehicle-heading">
          Sorry, we couldn't find your vehicle details
        </h2>
        <p className="no-vehicle-paragraph">
          We can’t give you an instant valuation this time. Leave your details
          below we'll get in touch with a valuation for your vehicle.
        </p>
        <Seperator />
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <GAHiddenInputs />
        <div className="no-vehicle-inner-container">
          <div className="input-label vehicle-type-input">
            <p className="no-vehicle-title">Vehicle type *</p>

            <div className="no-vehicle-details-container">
              <div>
                <label className={`no-vehicle-details ${radiosErrorClass}`}>
                  <div className="select-type">
                    <Input
                      type="radio"
                      value="Car"
                      errors={errors}
                      name="type"
                      register={register}
                      rules={{
                        required: true,
                      }}
                      checked={whichVehicleType === "Car"}
                      onChange={(e) => {
                        addWhichVehicleType(e.target.value)
                      }}
                    />
                  </div>
                  <div>
                    <h4 className="no-vehicle-condition-h">Car</h4>
                  </div>
                </label>
              </div>
            </div>

            <div className="no-vehicle-details-container">
              <div>
                <label className={`no-vehicle-details ${radiosErrorClass}`}>
                  <div className="select-type">
                    <Input
                      type="radio"
                      value="Van"
                      rules={{
                        required: true,
                      }}
                      name="type"
                      errors={errors}
                      register={register}
                      checked={whichVehicleType === "Van"}
                      onChange={(e) => {
                        addWhichVehicleType(e.target.value)
                      }}
                    />
                  </div>
                  <div>
                    <h4 className="no-vehicle-condition-h">Van</h4>
                  </div>
                </label>
              </div>
            </div>

            <div className="no-vehicle-details-container">
              <div>
                <label className={`no-vehicle-details ${radiosErrorClass}`}>
                  <div className="select-type">
                    <Input
                      type="radio"
                      value="Bike"
                      rules={{
                        required: true,
                      }}
                      name="type"
                      errors={errors}
                      register={register}
                      checked={whichVehicleType === "Bike"}
                      onChange={(e) => {
                        addWhichVehicleType(e.target.value)
                      }}
                    />
                  </div>
                  <div>
                    <h4 className="no-vehicle-condition-h">Bike</h4>
                  </div>
                </label>
              </div>
            </div>
            {errors && errors["type"] ? (
              <p className="vehicle-type-error-input">Please select a vehicle type</p>
            ) : null}
          </div>
          <div className="input-label">
            <p className="no-vehicle-title">Manufacturer *</p>
            <Input
              type="text"
              defaultValue={brand}
              name="manufacturer"
              placeholder="Enter your vehicle's manufacturer"
              register={register}
              rules={{
                required: errorMessage.vehicleManufacturer,
                minLength: {
                  value: 2,
                  message: errorMessage.vehicleManufacturer,
                },
                maxLength: {
                  value: 50,
                  message: errorMessage.vehicleManufacturer,
                },
              }}
              onChange={(e) =>
                addWhichVehicle({ ...whichVehicle, brand: e.target.value })
              }
              errors={errors}
            />
          </div>
          <div className="input-label">
            <p className="no-vehicle-title">Model *</p>
            <Input
              type="text"
              defaultValue={model}
              name="model"
              register={register}
              placeholder="Enter your vehicle's model"
              rules={{
                required: errorMessage.vehicleModel,
                minLength: {
                  value: 2,
                  message: errorMessage.vehicleModel,
                },
                maxLength: {
                  value: 50,
                  message: errorMessage.vehicleModel,
                },
              }}
              onChange={(e) =>
                addWhichVehicle({ ...whichVehicle, model: e.target.value })
              }
              errors={errors}
            />
          </div>
          <div className="input-label">
            <p className="no-vehicle-title">First name *</p>
            <Input
              type="text"
              defaultValue={firstName}
              name="firstName"
              register={register}
              placeholder="Enter your first name"
              rules={{
                required: errorMessage.firstName,
                minLength: {
                  value: 2,
                  message: errorMessage.firstName,
                },
                maxLength: {
                  value: 50,
                  message: errorMessage.firstName,
                },
              }}
              onChange={(e) =>
                addContactDetails({
                  ...contactDetails,
                  firstName: e.target.value,
                })
              }
              errors={errors}
            />
          </div>
          <div className="input-label">
            <p className="no-vehicle-title">Last name *</p>
            <Input
              type="text"
              defaultValue={lastName}
              name="lastName"
              register={register}
              placeholder="Enter your last name"
              rules={{
                required: errorMessage.lastName,
                minLength: {
                  value: 2,
                  message: errorMessage.lastName,
                },
                maxLength: {
                  value: 50,
                  message: errorMessage.lastName,
                },
              }}
              onChange={(e) =>
                addContactDetails({
                  ...contactDetails,
                  lastName: e.target.value,
                })
              }
              errors={errors}
            />
          </div>
          <div className="input-label">
            <p className="no-vehicle-title">Email *</p>
            <Input
              defaultValue={email}
              type="email"
              name="email"
              register={register}
              placeholder="Enter your email"
              rules={{
                required: errorMessage.email,
                pattern: {
                  value:
                    // eslint-disable-next-line no-useless-escape
                    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  message: errorMessage.email,
                },
              }}
              onChange={(e) =>
                addContactDetails({ ...contactDetails, email: e.target.value })
              }
              errors={errors}
            />
          </div>
          <div className="input-label">
            <p className="no-vehicle-title">Contact number *</p>
            <CountryFlag
              name="countryCode"
              register={register}
              countryCode={countryCode}
              onChange={(e) =>
                addContactDetails({
                  ...contactDetails,
                  countryCode: e.target.value,
                })
              }
            />
            <ContactNumber
              name="contactNumber"
              className="no-vehicle-contact-input"
              countryCode={countryCode}
              control={control}
              errors={errors}
              errorMessage={errorMessage.contactNumber}
            />
          </div>
          <div className="input-label dealership-input">
            <p className="no-vehicle-title postcode-title">Enter your postcode to help locate your nearest Lookers dealerships.</p>
            <p className="no-vehicle-title">Postcode *</p>
            <Input
              className="no-vehicle-postcode"
              defaultValue={postcode}
              type="text"
              register={register}
              name="postcode"
              placeholder="Enter your postcode"
              rules={{
                required: "Please enter your postcode",
                pattern: {
                  value:
                    // eslint-disable-next-line no-useless-escape
                    /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([AZa-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))[\s]*[0-9][A-Za-z]{2})$/,
                  message: errorMessage.postcode,
                },
              }}
              onChange={(e) => {
                addContactDetails({
                  ...contactDetails,
                  postcode: e.target.value,
                })
                if (Object.keys(errors).length === 0) {
                  setValidPostcode(true)
                  setPostCodeError(false)
                } else {
                  setValidPostcode(false)
                  setPostCodeError(true)
                }
                setValidPostCodeError(false)
              }}
              errors={errors}
            />
            {postCodeError && Object.keys(errors).length === 0 && !validPostcode ? (
              <div className="error-valid-postcode">
                <p>Please enter a valid postcode</p>
              </div>
            ) : null}
            {validPostCodeError ? (
              <div className="post-code-error">
                <p>Something went wrong. Please check your postcode for errors and try again, or try a different postcode.</p>
              </div>
            ) : null}
            <div className="button-container">
              <GeoLocation
                postCode={contactDetails.postcode}
                setPostCodeError={setPostCodeError}
                groupId={GroupIds[groupId]}
                addClosestDealerships={addClosestDealerships}
                closestDealerships={closestDealerships}
                dealershipCtaDisabled={dealershipCtaDisabled}
                setDealershipCtaDisabled={setDealershipCtaDisabled}
                rendersFindDealershipCta={true}
                setButtonClicked={setButtonClicked}
                validPostcode={validPostcode}
                buttonType="button"
                buttonId="no-vehicle-found-dealership-button"
                setFindMyDealershipClicked={setFindMyDealershipClicked}
                variant="inverted-blue"
              />
            </div>
            {isButtonClicked && !closestDealerships.length && postcode ? (
              <div className="dealership-loading-spinner">
                <DealershipLoader />
              </div>
            ) : null}
            {closestDealerships && closestDealerships.length ? (
              <div className="input-label select-dealership-dropdown">
                <Dropdown
                  label=""
                  defaultValue={dealership}
                  options={closestDealerships}
                  disabled={
                    !postCodeError && closestDealerships?.length > 0 ? false : true
                  }
                  placeholder="Select your dealership"
                  ref={
                    register &&
                    register({ required: "Please choose a dealership" })
                  }
                  name="dealership"
                  errors={errors}
                  onChange={(e: any) => {
                    const { target } = e
                    if (target && target.value) {
                      addAppointmentDetails({
                        ...appointmentDetails,
                        dealership: e.target.value,
                      })
                    }
                  }}
                />
              </div>
            ) : null}
            {isSubmitted && !dealership && !isFindMyDealershipClicked ?
              <div className="post-code-error">
                <p className="find-my-dealership-error-input">Please find and choose a dealership</p>
              </div> : null}
            {dealershipError ? (
              <div className="post-code-error">
                <p>{errorMessage.dealership}</p>
              </div>
            ) : null}
          </div>
        </div>
        <div className="no-vehicle-button">
          <Seperator className="top-seperator"/>
          <TermsAndConditions
            termsConditions={termsConditions}
            addTermsConditions={addTermsConditions}
            name="termsConditions"
            register={register}
            setValue={setValue}
            errors={errors}
          />
          <div>
            {errors && errors["termsConditions"] ? (
              <p className="terms-error-input">Please accept the terms</p>
            ) : null}
          </div>
          <Seperator className="bottom-seperator" />
          <Button
            onClick={() => {
              setSubmitted(true)
              if (checkForEmptyFields && Object.keys(errors).length === 0 && !dealershipGuidError && dealership) {
                getLead(history)
                history.push(`/vehicle-not-found-thank-you${checkPath(search)}`)
              }
            }}
            type="submit"
            variant="blue"
            id="request-valuation-button"
          >
            REQUEST VALUATION
          </Button>
        </div>
      </form>
      <TextLink text="Change vehicle" href={checkPath(search)} />
    </div>
  )
}

export default CouldntFindVehicle
